import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import type { AccountMetadataStatusSchema, Requisition as GCLRequisition } from "../gcl/models.js";
import type { Locale } from "../models.js";
import type { EntrepriseFiche } from "../pappers/models.js";
import type { PowensConnection } from "../powens/models.js";
import type { Envelope as SignatureApiEnvelope } from "../signatureApi/models.js";
import type { StripeCustomer, StripeSubscription } from "../stripe/models.js";
import type { SwanKyb } from "../swan/kyb.js";
import type { UserWithKyc as TreezorUser, Wallet as TreezorWallet } from "../treezor/models.js";
import type { AssignedSupervisors } from "./assignedSupervisors.js";
import type { AccountType } from "./index.js";
import type { AccountState } from "./state.js";

export const accountsFullPath = "accounts";
export const accountFullPath = (accountId: string): string => `${accountsFullPath}/${accountId}`;

export const accountUserRoleSchema = z.enum(["accountHolder"]);
export type AccountUserRole = z.infer<typeof accountUserRoleSchema>;

export type AccountUser = {
  role: AccountUserRole;
  addedAt: Timestamp;
};

export type Account = {
  name: string;
  createdAt: Timestamp | undefined;
  /** Open banking only (Bridge, Powens, GCL). */
  bankAccountId?: string;
  /** Open banking only (Bridge, Powens, GCL). */
  balance?: number;
  currency?: string;
  bank?: {
    name: string;
  };
  bridge_item?: {
    item_id: number;
    provider_id: number | undefined;
    status_code: number;
    status_code_info: string | null;
  };
  bridge_user_uuid?: string;
  bridge_item_created_at?: Timestamp;
  supervisorOrgId?: string;
  notifications?: {
    balance: {
      max: number | null;
      min: number | null;
    };
    transaction: {
      max: number | null;
      min?: number | null;
    };
    /**
     * Email the assigned supervisors (`assignedSupervisors`) once when the account has no activity
     * for `days` days. On pro accounts, only transfers sent and funds received count (direct
     * debits such as the Aircap subscription are ignored).
     */
    inactivity?: {
      enabled: boolean;
      days: number;
    };
  };
  /**
   * Last inactivity alert sent (server-only). `since` is the last activity of the alerted period:
   * no new alert until a newer activity moves it.
   */
  inactivityAlert?: {
    since: Timestamp;
    sentAt: Timestamp;
  };
  assignedSupervisors: AssignedSupervisors;
  externalApprovers?: string[];
  users?: Record<string, AccountUser>;
  /**
   * Keys of `users`. Needed for `array-contains` queries, composite indexes and list rules.
   */
  userIds?: string[];
  groupId: string | null;
  isArchived: boolean;
  accountType: AccountType;
  swan_onboarding?: {
    id: string;
    status: "Finalized" | "Invalid" | "Valid";
  };
  swan_account?: {
    id?: string;
    IBAN: string | null;
    IBANCreatedAt?: Timestamp;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
    BIC?: string;
    name: string;
    number: string;
    status?: "Suspended" | "Opened" | "Closing" | "Closed";
    statusInfo:
      | {
          status: "Suspended" | "Opened" | "Closing" | "Closed";
          reasonInfo?: {
            typeCloseAccountReason?: string;
            typeSuspendAccountReason?: string;
            message: string | null;
          };
          closedAt?: string;
          closingAt?: string;
        }
      | undefined;
    paymentLevel: "Unlimited" | "Limited";
    country: "FRA" | "DEU" | "ESP" | "NLD" | "ITA" | "BEL";
    currency: string;
    balances: Record<
      "available" | "pending" | "booked" | "reserved",
      {
        currency: string;
        value: string;
      }
    > | null;
  };
  swan_account_holder?: {
    id: string;
    name: string;
    status?: "Enabled" | "Suspended" | "Canceled";
    verification_status:
      "NotStarted" | "WaitingForInformation" | "Pending" | "Verified" | "Refused";
    /**
     * Numéro d'enregistrement de l'entreprise sourcé de Swan (= SIREN pour une entreprise française).
     * Sert de clé de lookup pour le webhook Pappers.
     */
    registrationNumber?: string;
  };
  swan_kyb?: SwanKyb;
  /** Informations Pappers de l'entreprise (projets Swan uniquement), maintenues via le webhook Pappers. */
  pappers?: {
    /**
     * SIREN réellement utilisé lors du dernier fetch Pappers. Peut différer de
     * `swan_account_holder.registrationNumber` si Swan a été mis à jour entre-temps.
     */
    siren: string;
    fiche: EntrepriseFiche;
    updatedAt: Timestamp;
  };
  /**
   * `undefined` or `null`: no transfer request required. `number`: require a transfer request
   * when `amount >= threshold` (even if threshold is `0`).
   */
  transferAmountThreshold?: number | null;
  powens_user_id?: number;
  powens_access_token_encrypted?: string;
  powens_connection?: PowensConnection & { deleted?: Timestamp };
  powens_connection_type?: "bank" | "wealth";
  operatorLanguage: Locale; // TODO: to rename to accountLanguage or just language
  /**
   * this is for debug purpose only. use bankAccountId, gcl_account_status, or gclAccount's institution_id instead
   */
  gcl_requisition?: GCLRequisition;
  gcl_account_status?: z.infer<typeof AccountMetadataStatusSchema>;
  pendingTransfersCount?: number;
  canUseSwanCards?: boolean;
  proAccountPrice?: number;
  contractType?: "signatureApi" | "custom" | null;
  contractTemplateId?: string;
  contractEnvelope?: SignatureApiEnvelope;
  subscriptionType?: "stripe" | null;
  stripeCustomer?: StripeCustomer;
  stripeSubscription?: StripeSubscription;
  _stripeSubscriptionCreation?: boolean; // internal flag to prevent concurrent creation of Stripe subscriptions for the same account
  lastStatus?: { status: AccountState["status"]; updatedAt: Timestamp };
  treezorCompany?: TreezorUser;
  treezorWallet?: TreezorWallet;
  /**
   * Admin review of the KYC, before submission to third party.
   * - `pending`: the user finished filling KYC information; an admin can review and submit to the third party.
   * - `refused`: an admin sent the KYC back to the user with a reason; the user can edit it again.
   * - `validated`: an admin submitted the KYC to the third party. `treezorCompany.kyc.kycStatus` then drives account state.
   * Each change is also recorded in the account's logs.
   */
  kycAdminReview?:
    | { status: "pending"; updatedAt: Timestamp }
    | { status: "refused"; reason: string; updatedAt: Timestamp }
    | { status: "validated"; updatedAt: Timestamp };
};

export const contactsPathFromAccount = `contacts`;
export const contactsFullPath = (accountId: string): string =>
  `${accountFullPath(accountId)}/${contactsPathFromAccount}`;
export const contactFullPath = (accountId: string, contactId: string): string =>
  `${contactsFullPath(accountId)}/${contactId}`;

export type Contact = {
  email: string;
  phone?: string;
  notificationsDisabled?: boolean;
  lastNotifiedAt?: Timestamp;
  createdAt: Timestamp | undefined;
};
